import { createContext, ReactNode, useContext, useEffect, useReducer } from 'react'
import { useAppSelector } from 'store'
import { parseErrorMessage, parseUrl } from './utils'
import { io, Socket as SocketClient } from 'socket.io-client'

export enum ConnectionStatus {
  CONNECTING = 'CONNECTING',
  NOT_CONFIGURE = 'NOT_CONFIGURE',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  ERROR = 'ERROR',
}

interface State {
  isConnected: boolean
  socket: SocketClient | null
  status: ConnectionStatus
  version: string | null
  message: string | null
}

interface InitResponse {
  success: boolean
  message: string
  version: string
}

type Action =
  | { type: 'SET_SOCKET'; payload: SocketClient | null }
  | { type: 'SET_STATUS'; payload: ConnectionStatus }
  | { type: 'SET_VERSION'; payload: string | null }
  | { type: 'SET_MESSAGE'; payload: string | null }

const initialState: State = {
  isConnected: false,
  socket: null,
  status: ConnectionStatus.CONNECTING,
  version: null,
  message: 'connecting',
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_SOCKET':
      return { ...state, socket: action.payload }
    case 'SET_STATUS':
      return { ...state, status: action.payload, isConnected: action.payload == ConnectionStatus.CONNECTED }
    case 'SET_VERSION':
      return { ...state, version: action.payload }
    case 'SET_MESSAGE':
      return { ...state, message: action.payload }
    default:
      return state
  }
}

const Context = createContext<State | undefined>(undefined)

export const DialerSocketProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const userState = useAppSelector((state) => state.user)
  const user = userState.user
  const dialerUrl = user?.dialerUrl || null
  const sip = user?.sip || null

  useEffect(() => {
    if (!dialerUrl || !sip) {
      dispatch({ type: 'SET_STATUS', payload: ConnectionStatus.NOT_CONFIGURE })
      dispatch({ type: 'SET_SOCKET', payload: null })
      dispatch({ type: 'SET_VERSION', payload: null })
      dispatch({ type: 'SET_MESSAGE', payload: 'Not configured' })
      return
    }

    const url = parseUrl(dialerUrl)
    if (!url) {
      dispatch({ type: 'SET_STATUS', payload: ConnectionStatus.ERROR })
      dispatch({ type: 'SET_MESSAGE', payload: 'Invalid URL' })
      return
    }

    const socket = io(url.origin, {
      transports: ['websocket'],
      query: { ts: new Date().getTime().toString() },
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      path: `${url.pathname.replace(/\/$/, '')}/socket.io`,
    })

    socket.on('connect', () => {
      socket.emit('init', { sip }, (response: InitResponse) => {
        if (response.success) {
          dispatch({ type: 'SET_SOCKET', payload: socket })
          dispatch({ type: 'SET_STATUS', payload: ConnectionStatus.CONNECTED })
          dispatch({ type: 'SET_VERSION', payload: response.version })
          dispatch({ type: 'SET_MESSAGE', payload: null })
        } else {
          socket.disconnect()
          dispatch({ type: 'SET_STATUS', payload: ConnectionStatus.ERROR })
          dispatch({ type: 'SET_MESSAGE', payload: response.message })
        }
      })
    })

    socket.on('disconnect', (reason) => {
      dispatch({ type: 'SET_STATUS', payload: ConnectionStatus.DISCONNECTED })
      dispatch({ type: 'SET_MESSAGE', payload: `Disconnected: ${reason}` })
    })

    socket.on('connect_error', (error) => {
      dispatch({ type: 'SET_STATUS', payload: ConnectionStatus.ERROR })
      dispatch({ type: 'SET_MESSAGE', payload: parseErrorMessage(error) })
    })

    socket.on('reconnect', () => {
      dispatch({ type: 'SET_STATUS', payload: ConnectionStatus.CONNECTED })
      dispatch({ type: 'SET_MESSAGE', payload: null })
    })

    socket.on('reconnect_error', (error) => {
      dispatch({ type: 'SET_STATUS', payload: ConnectionStatus.ERROR })
      dispatch({ type: 'SET_MESSAGE', payload: `Reconnect failed: ${parseErrorMessage(error)}` })
    })

    return () => {
      socket.disconnect()
      dispatch({ type: 'SET_SOCKET', payload: null })
      dispatch({ type: 'SET_STATUS', payload: ConnectionStatus.DISCONNECTED })
    }
  }, [dialerUrl, sip])

  return <Context.Provider value={state}>{children}</Context.Provider>
}

export const useDialerSocketContext = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error('useDialerSocketContext must be used within DialerSocketProvider')
  }
  return context
}
